<script setup>
import { ref, onMounted, computed, reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useCompaniesStore } from 'dashboard/stores/companies';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import CompanyFormModal from 'dashboard/components-next/Companies/CompanyFormModal.vue';
import CompanyAddressesAPI from 'dashboard/api/companyAddresses';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const companiesStore = useCompaniesStore();

const companyFormModalRef = ref(null);
const addressDialogRef = ref(null);
const isUpdating = ref(false);
const isSavingAddress = ref(false);
const editingAddress = ref(null);

const emptyAddressForm = () => ({
  label: '',
  address_line1: '',
  address_line2: '',
  city: '',
  postal_code: '',
  region: '',
  country: '',
});

const addressForm = reactive(emptyAddressForm());

const company = computed(() => {
  const companyId = route.params.companyId;
  return companiesStore.getCompany(companyId);
});

const addresses = computed(() => company.value?.addresses || []);

const addressDialogTitle = computed(() =>
  editingAddress.value
    ? t('COMPANIES.DETAILS.EDIT_ADDRESS')
    : t('COMPANIES.DETAILS.ADD_ADDRESS')
);

const isAddressFormValid = computed(
  () => addressForm.address_line1.trim().length > 0
);

const formatAddressDisplay = address => {
  if (address.address) return address.address;
  return [
    address.address_line1,
    address.address_line2,
    [address.postal_code, address.city].filter(Boolean).join(' '),
    [address.region, address.country].filter(Boolean).join(', '),
  ]
    .filter(Boolean)
    .join(', ');
};

const goBack = () => {
  if (window.history.state?.back || window.history.length > 1) {
    router.back();
  } else {
    router.push(`/app/accounts/${route.params.accountId}/companies?page=1`);
  }
};

const openEditModal = () => {
  companyFormModalRef.value?.open();
};

const handleUpdateCompany = async payload => {
  isUpdating.value = true;
  try {
    await companiesStore.update({
      ...payload,
      id: company.value.id,
    });
    useAlert(t('COMPANIES.FORM.UPDATE_SUCCESS'));
    companyFormModalRef.value?.close();
    await companiesStore.show(route.params.companyId);
  } catch (error) {
    const message = error.message || t('COMPANIES.FORM.UPDATE_ERROR');
    useAlert(message);
    console.error('Error updating company:', error);
  } finally {
    isUpdating.value = false;
  }
};

const resetAddressForm = () => {
  Object.assign(addressForm, emptyAddressForm());
  editingAddress.value = null;
};

const openAddAddress = () => {
  resetAddressForm();
  addressDialogRef.value?.open();
};

const openEditAddress = address => {
  editingAddress.value = address;
  addressForm.label = address.label || '';
  addressForm.address_line1 = address.address_line1 || address.address || '';
  addressForm.address_line2 = address.address_line2 || '';
  addressForm.city = address.city || '';
  addressForm.postal_code = address.postal_code || '';
  addressForm.region = address.region || '';
  addressForm.country = address.country || '';
  addressDialogRef.value?.open();
};

const refreshCompany = async () => {
  await companiesStore.show(route.params.companyId);
};

const handleSaveAddress = async () => {
  if (!isAddressFormValid.value || !company.value) return;

  isSavingAddress.value = true;
  const payload = {
    label: addressForm.label.trim() || null,
    address_line1: addressForm.address_line1.trim(),
    address_line2: addressForm.address_line2.trim() || null,
    city: addressForm.city.trim() || null,
    postal_code: addressForm.postal_code.trim() || null,
    region: addressForm.region.trim() || null,
    country: addressForm.country.trim() || null,
  };

  try {
    if (editingAddress.value) {
      await CompanyAddressesAPI.update(
        company.value.id,
        editingAddress.value.id,
        payload
      );
    } else {
      await CompanyAddressesAPI.create(company.value.id, payload);
    }
    useAlert(t('COMPANIES.DETAILS.ADDRESS_SAVED'));
    addressDialogRef.value?.close();
    resetAddressForm();
    await refreshCompany();
  } catch (error) {
    useAlert(t('COMPANIES.DETAILS.ADDRESS_SAVE_ERROR'));
    console.error('Error saving address:', error);
  } finally {
    isSavingAddress.value = false;
  }
};

const handleDeleteAddress = async address => {
  if (!company.value) return;

  try {
    await CompanyAddressesAPI.delete(company.value.id, address.id);
    useAlert(t('COMPANIES.DETAILS.ADDRESS_DELETED'));
    await refreshCompany();
  } catch (error) {
    useAlert(t('COMPANIES.DETAILS.ADDRESS_DELETE_ERROR'));
    console.error('Error deleting address:', error);
  }
};

onMounted(async () => {
  if (route.params.companyId) {
    await companiesStore.show(route.params.companyId);
  }
});
</script>

<template>
  <div class="flex flex-col w-full h-full bg-n-surface-1">
    <header class="sticky top-0 z-10 px-6 lg:px-0">
      <div class="w-full max-w-[60rem] mx-auto">
        <div class="flex items-center justify-between w-full h-20 gap-2">
          <button
            class="inline-flex items-center gap-2 text-sm text-n-slate-11 hover:text-n-slate-12 transition-colors"
            @click="goBack"
          >
            <span class="i-lucide-arrow-left size-4" />
            {{ t('COMPANIES.DETAILS.BACK_TO_LIST') }}
          </button>
          <Button
            v-if="company"
            size="sm"
            icon="i-lucide-pencil"
            :label="t('COMPANIES.DETAILS.EDIT')"
            @click="openEditModal"
          />
        </div>
      </div>
    </header>
    <main class="flex-1 px-6 overflow-y-auto lg:px-0">
      <div class="w-full max-w-[60rem] mx-auto py-4">
        <div v-if="company" class="flex flex-col gap-6">
          <div class="flex flex-col gap-4">
            <h1 class="text-2xl font-semibold text-n-slate-12">
              {{ company.name || t('COMPANIES.UNNAMED') }}
            </h1>
            <div v-if="company.domain" class="flex items-center gap-2">
              <span class="i-lucide-globe size-4 text-n-slate-11" />
              <span class="text-sm text-n-slate-11">{{ company.domain }}</span>
            </div>
            <p v-if="company.description" class="text-sm text-n-slate-11">
              {{ company.description }}
            </p>
          </div>

          <div class="flex flex-col gap-4 pt-6 border-t border-n-strong">
            <h2 class="text-lg font-medium text-n-slate-12">
              {{ t('COMPANIES.DETAILS.INFORMATION') }}
            </h2>
            <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div v-if="company.vatNumber" class="flex flex-col gap-1">
                <span class="text-xs font-medium text-n-slate-11">
                  {{ t('COMPANIES.FORM.FIELDS.VAT_NUMBER') }}
                </span>
                <span class="text-sm text-n-slate-12">
                  {{ company.vatNumber }}
                </span>
              </div>
              <div v-if="company.hubspotId" class="flex flex-col gap-1">
                <span class="text-xs font-medium text-n-slate-11">
                  {{ t('COMPANIES.FORM.FIELDS.HUBSPOT_ID') }}
                </span>
                <span class="text-sm text-n-slate-12">
                  {{ company.hubspotId }}
                </span>
              </div>
              <div v-if="company.teamleaderId" class="flex flex-col gap-1">
                <span class="text-xs font-medium text-n-slate-11">
                  {{ t('COMPANIES.FORM.FIELDS.TEAMLEADER_ID') }}
                </span>
                <span class="text-sm text-n-slate-12">
                  {{ company.teamleaderId }}
                </span>
              </div>
              <div class="flex flex-col gap-1">
                <span class="text-xs font-medium text-n-slate-11">
                  {{ t('COMPANIES.DETAILS.CONTACTS_COUNT') }}
                </span>
                <span class="text-sm text-n-slate-12">
                  {{ company.contactsCount || 0 }}
                </span>
              </div>
            </div>
          </div>

          <div class="flex flex-col gap-4 pt-6 border-t border-n-strong">
            <div class="flex items-center justify-between gap-2">
              <h2 class="text-lg font-medium text-n-slate-12">
                {{ t('COMPANIES.DETAILS.ADDRESSES') }}
              </h2>
              <Button
                size="sm"
                icon="i-lucide-plus"
                :label="t('COMPANIES.DETAILS.ADD_ADDRESS')"
                @click="openAddAddress"
              />
            </div>

            <div
              v-if="!addresses.length"
              class="text-sm text-n-slate-11 py-2"
            >
              {{ t('COMPANIES.DETAILS.ADDRESSES_EMPTY') }}
            </div>

            <ul v-else class="flex flex-col gap-3">
              <li
                v-for="address in addresses"
                :key="address.id"
                class="flex items-start justify-between gap-4 p-4 rounded-xl outline outline-1 outline-n-container bg-n-solid-2"
              >
                <div class="flex flex-col gap-1 min-w-0">
                  <span
                    v-if="address.label"
                    class="text-xs font-medium text-n-slate-11"
                  >
                    {{ address.label }}
                  </span>
                  <span class="text-sm text-n-slate-12">
                    {{ formatAddressDisplay(address) }}
                  </span>
                </div>
                <div class="flex items-center gap-1 shrink-0">
                  <Button
                    size="sm"
                    variant="ghost"
                    icon="i-lucide-pencil"
                    @click="openEditAddress(address)"
                  />
                  <Button
                    size="sm"
                    variant="ghost"
                    color="ruby"
                    icon="i-lucide-trash-2"
                    @click="handleDeleteAddress(address)"
                  />
                </div>
              </li>
            </ul>
          </div>
        </div>
        <div v-else class="flex items-center justify-center py-10">
          <span class="text-n-slate-11">{{ t('COMPANIES.LOADING') }}</span>
        </div>
      </div>
    </main>

    <CompanyFormModal
      ref="companyFormModalRef"
      :company="company"
      :is-loading="isUpdating"
      @save="handleUpdateCompany"
    />

    <Dialog
      ref="addressDialogRef"
      type="edit"
      :title="addressDialogTitle"
      :confirm-button-label="t('COMPANIES.FORM.UPDATE')"
      :cancel-button-label="t('COMPANIES.FORM.CANCEL')"
      :disable-confirm-button="!isAddressFormValid"
      :is-loading="isSavingAddress"
      width="lg"
      @confirm="handleSaveAddress"
      @close="resetAddressForm"
    >
      <form class="flex flex-col gap-4 p-1" @submit.prevent="handleSaveAddress">
        <Input
          v-model="addressForm.label"
          :label="t('COMPANIES.FORM.FIELDS.ADDRESS_LABEL')"
          :placeholder="t('COMPANIES.FORM.PLACEHOLDERS.ADDRESS_LABEL')"
        />
        <Input
          v-model="addressForm.address_line1"
          :label="t('COMPANIES.FORM.FIELDS.ADDRESS_LINE1')"
          :placeholder="t('COMPANIES.FORM.PLACEHOLDERS.ADDRESS_LINE1')"
          required
        />
        <Input
          v-model="addressForm.address_line2"
          :label="t('COMPANIES.FORM.FIELDS.ADDRESS_LINE2')"
          :placeholder="t('COMPANIES.FORM.PLACEHOLDERS.ADDRESS_LINE2')"
        />
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Input
            v-model="addressForm.city"
            :label="t('COMPANIES.FORM.FIELDS.CITY')"
            :placeholder="t('COMPANIES.FORM.PLACEHOLDERS.CITY')"
          />
          <Input
            v-model="addressForm.postal_code"
            :label="t('COMPANIES.FORM.FIELDS.POSTAL_CODE')"
            :placeholder="t('COMPANIES.FORM.PLACEHOLDERS.POSTAL_CODE')"
          />
          <Input
            v-model="addressForm.region"
            :label="t('COMPANIES.FORM.FIELDS.REGION')"
            :placeholder="t('COMPANIES.FORM.PLACEHOLDERS.REGION')"
          />
        </div>
        <Input
          v-model="addressForm.country"
          :label="t('COMPANIES.FORM.FIELDS.COUNTRY')"
          :placeholder="t('COMPANIES.FORM.PLACEHOLDERS.COUNTRY')"
        />
      </form>
    </Dialog>
  </div>
</template>
